import { readItemUuidSlot } from '../compile/inventory-raw-readers';
import type { DefinitionReaders, DefinitionRow } from './location-type-ambience';
import { creatorSavedChoice } from '../character-creator/panels';

export interface FormCardReaders extends DefinitionReaders {
    entries(row: unknown, key: string): unknown[];
}
export type FormOption = { id: string; name: string };
export const preparedLayers = ['spell_prepared_1', 'spell_prepared_2', 'spell_prepared_3'] as const;
export const shapeshiftChoices = [1, 2, 3, 4].map(n => `user_choice_${n}_shapeshift`);
export const formCardUuid = (value: unknown): string => readItemUuidSlot(value).toUpperCase();

export function conditionalShapeshift(source: DefinitionRow, read: FormCardReaders): string {
    const value = read.layer(source, 'shapeshift');
    const text = typeof value === 'string' ? value : Array.isArray(value) ? String(value[0] ?? '') : '';
    return text.includes('[') ? text : '';
}

export function shapeshiftChoiceSpec(source: DefinitionRow, layer: string, read: FormCardReaders): string {
    const entries = read.entries(source, layer), first = entries[0];
    return entries.length === 1 && typeof first === 'string' && (first === 'creature' || first.includes('[')) ? first : '';
}

export function shapeshiftNeedsCandidates(source: DefinitionRow, read: FormCardReaders): boolean {
    return !!conditionalShapeshift(source, read) || shapeshiftChoices.some(layer => !!shapeshiftChoiceSpec(source, layer, read));
}

/** Brackets separated by pipes are simultaneous constraints; speed values are maxima. */
export function shapeshiftOptions(spec: string, candidates: readonly DefinitionRow[], read: FormCardReaders): FormOption[] {
    const conditions = [...spec.matchAll(/\[(creature_type|cr_max|speed_swimming|speed_flying):([^\]]*)\]/g)];
    const fraction = (value: unknown): number => {
        const text = String(value ?? '0').trim();
        const parts = text.split('/');
        return parts.length === 2 ? Number(parts[0]) / Number(parts[1]) : Number(text);
    };
    return candidates.filter(row => conditions.every(([, key, bound]) => {
        const value = read.layer(row, key === 'cr_max' ? 'cr' : key!);
        if (key === 'creature_type') return (formCardUuid(value) || String(value ?? '').toUpperCase())
            === (formCardUuid(bound) || String(bound).toUpperCase());
        return fraction(value) <= fraction(bound);
    })).map(row => ({ id: formCardUuid(read.item(row, 'item_id')), name: String(read.item(row, 'item_name') ?? '') }))
        .filter(option => option.id !== '' && option.name !== '');
}

export function formCardReferenceIds(source: DefinitionRow, read: FormCardReaders): string[] {
    return [...new Set([...preparedLayers, 'shapeshift', ...shapeshiftChoices]
        .flatMap(key => read.entries(source, key).map(formCardUuid)).filter(Boolean))];
}

/** References, including layer-less shells, always resolve through the finalized definition owner. */
export function preparedSpellGroups(source: DefinitionRow, definitions: ReadonlyMap<string, DefinitionRow | null>, read: FormCardReaders) {
    return preparedLayers.flatMap((layer, index) => {
        const entries = read.entries(source, layer);
        if (!entries.length) return [];
        const levels: FormOption[][] = Array.from({ length: 11 }, () => []);
        for (const entry of entries) {
            const id = formCardUuid(entry), definition = definitions.get(id);
            if (!id || !definition) throw new Error('Prepared spell definition unavailable. Please retry.');
            const value = read.layer(definition, 'spell_level');
            if (value === null || value === undefined || value === '') throw new Error('Prepared spell level unavailable. Please retry.');
            const level = Number(value);
            if (Number.isInteger(level) && level >= 0 && level <= 10)
                levels[level]!.push({ id, name: String(read.item(definition, 'item_name') ?? '') });
        }
        return [{ layer, label: index ? ` (List ${index + 1})` : '', levels }];
    });
}

/** Share the creator's existing exact/source and cross-slot saved-choice reader. */
export function savedShapeshiftChoice(rows: readonly DefinitionRow[], layer: string, source: string, exact = ''): string {
    return formCardUuid(creatorSavedChoice([...rows], layer, source, exact));
}
